import { useState, useCallback } from 'react';
import { AxiosError } from 'axios';
import { toast } from 'react-hot-toast';

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

interface UseApiOptions {
  onSuccess?: (data: unknown) => void;
  onError?: (error: Error) => void;
  showToast?: boolean;
  successMessage?: string;
  errorMessage?: string;
}

/**
 * Custom hook for making API requests with better error handling and type safety
 * @param apiFunction - The API function to execute
 * @param options - Configuration options for the hook
 * @returns Object containing data, loading state, error, and execute function
 */
export function useApi<T extends (...args: unknown[]) => Promise<unknown>>(
  apiFunction: T,
  options: UseApiOptions = {}
): {
  loading: boolean;
  error: Error | null;
  execute: (...args: Parameters<T>) => Promise<ReturnType<T>>;
} {
  const [state, setState] = useState<UseApiState<ReturnType<T>>>({
    data: null,
    loading: false,
    error: null,
  });

  const execute = useCallback(
    async (...args: Parameters<T>): Promise<ReturnType<T>> => {
      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        const response = await apiFunction(...args);

        setState({
          data: response as ReturnType<T>,
          loading: false,
          error: null,
        });

        if (options.onSuccess) {
          options.onSuccess(response);
        }

        if (options.showToast && options.successMessage) {
          toast.success(options.successMessage);
        }

        return response as ReturnType<T>;
      } catch (error) {
        const axiosError = error as AxiosError;
        const errorMessage =
          (axiosError.response?.data as { message?: string })?.message ||
          options.errorMessage ||
          'An error occurred';

        setState({
          data: null,
          loading: false,
          error: axiosError,
        });

        if (options.onError) {
          options.onError(axiosError);
        }

        if (options.showToast) {
          toast.error(errorMessage);
        }

        throw axiosError;
      }
    },
    [apiFunction, options]
  );

  return {
    loading: state.loading,
    error: state.error,
    execute,
  };
}

/**
 * Custom hook for making API requests with automatic toast notifications
 * @param apiFunction - The API function to execute
 * @param successMessage - Message to show on success
 * @returns Object containing data, loading state, error, and execute function
 */
export function useApiWithToast<
  T extends (...args: unknown[]) => Promise<unknown>,
>(
  apiFunction: T,
  successMessage: string
): {
  loading: boolean;
  error: Error | null;
  execute: (...args: Parameters<T>) => Promise<ReturnType<T>>;
} {
  return useApi(apiFunction, {
    showToast: true,
    successMessage,
    errorMessage: 'An error occurred',
  });
}
